import { get, httpDelete, post, put } from 'api/client';
import config from 'config';
import { getExtendedTimeout } from 'utils/request';
import RequestHeaders from 'app/constants/requestHeaders';
import { resolveReportDownloadRedirectUrl } from 'api/analytics-reports/resolveReportDownloadRedirectUrl';
import {
  isAnalyticsPdfExportCompleted,
  isHttpUrl,
  resolveAnalyticsReportPdfDownloadUrl,
} from 'app/utils/reportPdfExport';
import type {
  AnalyticsReportsListParams,
  AnalyticsReportsListResponse,
  CreateReportPayload,
  CreateReportSchedulePayload,
  CreateReportShareTokenPayload,
  DuplicateReportPayload,
  GenerateReportPayload,
  GetReportShareTokensParams,
  ReportAutomation,
  ReportFormat,
  ReportPdfExport,
  ReportPreviewPayload,
  ReportResource,
  ReportRun,
  ReportShareToken,
  ReportTemplate,
} from './types';

const BASE_PATH = '/reports';

const ANALYTICS_REPORTS_AXIOS_CONFIG = {
  baseURL: config.API_SERVICE_ENDPOINT,
  timeout: getExtendedTimeout(5),
} as const;

const ANALYTICS_REPORTS_ARRAY_QUERY_KEYS = new Set(['page_ids']);

function serializeAnalyticsReportsParams(
  params: Record<string, unknown>,
): string {
  const segments: string[] = [];

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue;
      }

      const queryName = ANALYTICS_REPORTS_ARRAY_QUERY_KEYS.has(key)
        ? `${key}[]`
        : encodeURIComponent(key);

      for (const item of value) {
        segments.push(`${queryName}=${encodeURIComponent(String(item))}`);
      }
      continue;
    }

    segments.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
    );
  }

  return segments.join('&');
}

function analyticsReportsGetParams(params: AnalyticsReportsListParams) {
  return {
    params,
    paramsSerializer: serializeAnalyticsReportsParams,
  };
}

export interface AnalyticsReportPreviewAuth {
  jwtToken?: string;
  previewToken?: string;
  accountId?: number;
}

const AnalyticsReportsApi = {
  getTemplates(): Promise<ReportTemplate[]> {
    return get<ReportTemplate[]>(
      `${BASE_PATH}/templates`,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  getReports(
    params: AnalyticsReportsListParams,
  ): Promise<AnalyticsReportsListResponse> {
    return get<AnalyticsReportsListResponse>(BASE_PATH, {
      ...ANALYTICS_REPORTS_AXIOS_CONFIG,
      ...analyticsReportsGetParams(params),
    });
  },

  getReport(reportId: number): Promise<ReportResource> {
    return get<ReportResource>(
      `${BASE_PATH}/${reportId}`,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  getReportPreview(
    reportId: number,
    auth?: AnalyticsReportPreviewAuth,
  ): Promise<ReportPreviewPayload> {
    const headers: Record<string, string> = {};

    if (auth?.accountId !== undefined) {
      headers[RequestHeaders.AccountId] = String(auth.accountId);
    }

    if (auth?.jwtToken) {
      headers[RequestHeaders.HttpJwtToken] = auth.jwtToken;
      headers[RequestHeaders.Authorization] = `Bearer ${auth.jwtToken}`;
    }

    return get<ReportPreviewPayload>(`${BASE_PATH}/${reportId}/preview`, {
      ...ANALYTICS_REPORTS_AXIOS_CONFIG,
      params: {
        include: 'time_series',
        ...(auth?.previewToken ? { token: auth.previewToken } : {}),
      },
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    });
  },

  createReport(payload: CreateReportPayload): Promise<ReportResource> {
    return post<ReportResource>(
      BASE_PATH,
      payload,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  updateReport(
    reportId: number,
    payload: CreateReportPayload,
  ): Promise<ReportResource> {
    return put<ReportResource>(
      `${BASE_PATH}/${reportId}`,
      payload,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  duplicateReport(
    reportId: number,
    payload: DuplicateReportPayload = {},
  ): Promise<ReportResource> {
    return post<ReportResource>(
      `${BASE_PATH}/${reportId}/duplicate`,
      payload,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  getShareTokens(
    reportId: number,
    params: GetReportShareTokensParams = {},
  ): Promise<ReportShareToken[]> {
    return get<ReportShareToken[]>(`${BASE_PATH}/${reportId}/share-tokens`, {
      ...ANALYTICS_REPORTS_AXIOS_CONFIG,
      params,
    });
  },

  createShareToken(
    reportId: number,
    payload: CreateReportShareTokenPayload = {},
  ): Promise<ReportShareToken> {
    return post<ReportShareToken>(
      `${BASE_PATH}/${reportId}/share-tokens`,
      payload,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  generateReport(
    reportId: number,
    payload: GenerateReportPayload,
  ): Promise<ReportRun> {
    return post<ReportRun>(
      `${BASE_PATH}/${reportId}/generate`,
      payload,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  getReportRun(reportId: number, runId: number): Promise<ReportRun> {
    return get<ReportRun>(
      `${BASE_PATH}/${reportId}/runs/${runId}`,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  async resolveReportDownloadUrl(
    reportId: number,
    format: ReportFormat = 'pdf',
  ): Promise<string | null> {
    const report = await this.getReport(reportId);
    const latestRun =
      format === 'pdf' ? report.latest_runs?.pdf : report.latest_runs?.csv;

    if (!latestRun || latestRun.status !== 'completed') {
      return null;
    }

    if (isHttpUrl(latestRun.download_url)) {
      return latestRun.download_url;
    }

    return resolveReportDownloadRedirectUrl(reportId, format);
  },

  async getPdfExport(reportId: number): Promise<ReportPdfExport> {
    const report = await this.getReport(reportId);
    const pdfRun = report.latest_runs?.pdf;

    if (!pdfRun) {
      return { url: null, status: 'unavailable' };
    }

    if (!isAnalyticsPdfExportCompleted(report.latest_runs)) {
      return { url: null, status: pdfRun.status };
    }

    const urlFromReport = resolveAnalyticsReportPdfDownloadUrl(
      report.latest_runs,
    );
    const url =
      urlFromReport ??
      (await resolveReportDownloadRedirectUrl(reportId, 'pdf'));

    return {
      url,
      status: pdfRun.status,
    };
  },

  createReportSchedule(
    reportId: number,
    payload: CreateReportSchedulePayload,
  ): Promise<ReportAutomation> {
    return post<ReportAutomation>(
      `${BASE_PATH}/${reportId}/schedules`,
      payload,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  updateReportSchedule(
    reportId: number,
    scheduleId: number,
    payload: CreateReportSchedulePayload,
  ): Promise<ReportAutomation> {
    return put<ReportAutomation>(
      `${BASE_PATH}/${reportId}/schedules/${scheduleId}`,
      payload,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  deleteReportSchedule(reportId: number, scheduleId: number): Promise<void> {
    return httpDelete<void>(
      `${BASE_PATH}/${reportId}/schedules/${scheduleId}`,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },

  deleteReport(reportId: number): Promise<void> {
    return httpDelete<void>(
      `${BASE_PATH}/${reportId}`,
      ANALYTICS_REPORTS_AXIOS_CONFIG,
    );
  },
};

export default AnalyticsReportsApi;
